import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, RefreshCw, Smartphone, Sparkles, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const INR = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmt = (v) => { if (!v) return "—"; try { return new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

const TYPE_META = {
  renewal: { icon: RefreshCw, cls: "bg-emerald-100 text-emerald-700", label: "Renewal" },
  app_package: { icon: Smartphone, cls: "bg-fuchsia-100 text-fuchsia-700", label: "App Package" },
  addon: { icon: Sparkles, cls: "bg-violet-100 text-violet-700", label: "Paid Addon" },
  signup: { icon: Sparkles, cls: "bg-sky-100 text-sky-700", label: "Subscription" },
};

function StatusBadge({ status }) {
  const s = (status || "").toUpperCase();
  const cls = s === "PAID" || s === "PROVISIONED" || s === "COMPLETED"
    ? "bg-emerald-100 text-emerald-700"
    : s === "PENDING" || s === "CREATED" || s === "ACTIVE"
      ? "bg-amber-100 text-amber-700"
      : s === "CANCELLED" || s === "EXPIRED"
        ? "bg-slate-100 text-slate-600"
        : "bg-slate-100 text-slate-600";
  const label = s === "ACTIVE" ? "PENDING" : (s || "—");
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

export default function AdminBilling() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/merchant/billing/history")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(extractErr(e)))
      .finally(() => setLoading(false));
  }, []);

  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><Receipt className="w-6 h-6 text-slate-700" /> {t("billing.title")}</h1>
        <p className="text-slate-500 text-sm mt-1">{t("billing.subtitle")}{data?.gstin ? ` · GSTIN ${data.gstin} · ${data.company}` : ""}</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <Receipt className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="mt-3 text-slate-500 text-sm">No payments yet. Your subscription and renewal history will appear here.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <div className="col-span-4">Payment</div>
            <div className="col-span-3">Date</div>
            <div className="col-span-2 text-right">GST</div>
            <div className="col-span-2 text-right">Total</div>
            <div className="col-span-1 text-right">Status</div>
          </div>
          <div className="divide-y divide-slate-100">
            {items.map((it, i) => {
              const meta = TYPE_META[it.type] || TYPE_META.signup;
              const Icon = meta.icon;
              return (
                <div key={it.order_id || i} className="grid grid-cols-2 md:grid-cols-12 gap-3 px-5 py-4 items-center" data-testid={`billing-row-${i}`}>
                  <div className="col-span-2 md:col-span-4 flex items-center gap-3 min-w-0">
                    <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.cls}`}><Icon className="w-4 h-4" /></span>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 text-sm truncate">{it.title}</div>
                      <div className="text-[11px] text-slate-400 font-mono-nums truncate">{it.order_id}</div>
                    </div>
                  </div>
                  <div className="md:col-span-3 text-sm text-slate-600 font-mono-nums">{fmt(it.paid_at || it.created_at)}</div>
                  <div className="md:col-span-2 text-right text-sm text-slate-500 font-mono-nums">{INR(it.gst_amount)}</div>
                  <div className="md:col-span-2 text-right font-bold text-slate-900 font-mono-nums">{INR(it.amount)}</div>
                  <div className="md:col-span-1 flex md:justify-end"><StatusBadge status={it.status} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
